<script setup lang="ts">
import { useUserStore } from '@/stores/modules/user'
import type { NestMonsterType } from '@/types/monster'
import { socketEmitAsync } from '@/utils/web-socket'
import { QuestionFilled, Warning } from '@element-plus/icons-vue'
import { ref } from 'vue'
import { useRoomStore } from '@/stores/modules/room'
import { crackRank } from '../data'
import { onMounted } from 'vue'
import DrageonMountainRank from './DrageonMountainRank.vue'
import { useCommonStore } from '@/stores/modules/common'

const envMode = import.meta.env.VITE_ENV_MODE

const userStore = useUserStore()
const commonStore = useCommonStore()
const roomStore = useRoomStore()

defineProps<{ monster?: NestMonsterType }>()
const emits = defineEmits(['getMonster', 'start', 'giveUp', 'showRoom', 'rankChange'])

const rank = ref(1)
const localKey = `war-soul-${envMode === 'test' ? 'test-' : ''}drageon-mountain-rank`
function handleChangeRank(val: number) {
  rank.value = val
  localStorage.setItem(localKey, String(rank.value))
  emits('rankChange', rank.value)
}
async function handleStartDrageonMountain() {
  if (!userStore.drageonMountainTimes) return ElMessage.warning('您的挑战次数不足')
  if (roomStore.id) return start()
  ElMessageBox.confirm(
    '<p class="affix">确定<span class="hp">独自</span>进行【<span class="hp">魔龙山巅</span>】<span class="info">战斗</span>吗？</p>',
    '提示',
    {
      type: 'warning',
      appendTo: 'body',
      dangerouslyUseHTMLString: true,
      confirmButtonClass: 'is-plain',
    },
  ).then(() => start())

  async function start() {
    const res = await socketEmitAsync<NestMonsterType>({ type: 'startDrageonMountainFight', data: { rank: rank.value } })
    roomShow.value = false
    roomStore.clearRoomInfo()
    emits('start', { ...res, type: 'drageonMountain' })
  }
}
const roomShow = ref(false)
function handleOpenRoom() {
  emits('showRoom')
}

const rankShow = ref(false)

onMounted(() => {
  const localRank = localStorage.getItem(localKey)
  if (localRank) rank.value = Number(localRank)
  emits('rankChange', rank.value)
})
</script>

<template>
  <div class="border-wrap relic-ruin">
    <h4 class="hp">
      魔龙山巅
      <div class="help-info" style="margin-left: 5px">
        <el-popover placement="top" width="100%" trigger="click">
          <template #reference>
            <el-icon size="20"><QuestionFilled /></el-icon>
          </template>
          <div class="affix" style="max-height: 60vh; overflow: auto">
            <h4>魔龙山巅说明</h4>
            <p>1. 魔龙山巅<span class="info">每周日0点刷新</span>，刷新后将重置奖励次数。</p>
            <p>
              2. 您需要在<span class="info">5分钟</span>的时间内通过挑战，战斗时支持<span class="info">切换装备即刻生效</span
              >，在合适的时机切换装备是通关的关键。
            </p>
            <p>3. 您可以自由选择挑战难度，随着难度增加奖励也将增加。</p>
            <p>4. 魔龙山巅支持<span class="info">双人组队</span>，当组队挑战时，怪物的血量增加<span class="grow">0.9</span>倍。</p>
            <p>
              5. 通关后可获得保底<span class="info">超限</span><span class="darkGold">暗金</span>及<span class="info">提纯石</span
              >用以提升刻印属性评分。
            </p>
            <p>6. 副本各项信息如表格所示：</p>
            <table border style="width: 100%; text-align: center">
              <tr>
                <td width="150">参考信息</td>
                <td class="normal">简单</td>
                <td class="race">普通</td>
                <td class="epic">困难</td>
              </tr>
              <tr>
                <td>保底超限</td>
                <td>1</td>
                <td>2</td>
                <td>3</td>
              </tr>
              <tr>
                <td>提纯石</td>
                <td>1</td>
                <td>3</td>
                <td>5</td>
              </tr>
            </table>
          </div>
        </el-popover>
      </div>
    </h4>
    <p style="display: flex; align-items: center; color: red">
      <el-icon style="margin-right: 5px"><Warning /></el-icon>
      <span>终极副本，难度极高</span>
    </p>
    <p style="display: flex; align-items: center">
      难度：
      <el-select size="small" v-model="rank" :class="crackRank[rank].class" style="width: 90px" @change="handleChangeRank">
        <el-option v-for="item of [1, 2, 3]" :key="item" :label="crackRank[item].name" :value="item">
          <span :class="crackRank[item].class">{{ crackRank[item].name }}</span>
        </el-option>
      </el-select>
    </p>
    <p>剩余挑战次数：{{ Math.max(userStore.drageonMountainTimes, 0) }}</p>
    <p>剩余奖励次数：{{ userStore.drageonMountainRewardTimes }}</p>
    <p>
      本周怪物：
      <CommonMonsterDetail :monster="monster" icon="" detailStyles="max-height: 350px; overflow: auto" />
    </p>
    <div style="margin: 10px 0">
      <template v-if="userStore.endlessTowerLevel < 20">
        <el-button type="success" plain disabled> 每周通过无尽之塔 20层后解锁 </el-button>
      </template>
      <template v-else>
        <el-button type="success" plain @click="() => handleStartDrageonMountain()" :disabled="Boolean(roomStore.id)">独自战斗</el-button>
        <el-button type="primary" plain @click="handleOpenRoom">组队战斗</el-button>
      </template>
      <el-button type="primary" plain @click="rankShow = true">排行</el-button>
    </div>
  </div>

  <el-dialog v-model="rankShow" width="100%" style="max-width: 500px">
    <DrageonMountainRank />
    <template #footer>
      <p style="text-align: center">
        <el-button plain @click="rankShow = false" size="small">关闭</el-button>
      </p>
    </template>
  </el-dialog>
</template>

<style lang="scss" scoped>
:deep() {
  .normal {
    .el-select__selected-item {
      span {
        color: #888084;
      }
    }
  }
  .race {
    .el-select__selected-item {
      span {
        color: #3e62ed;
      }
    }
  }
  .epic {
    .el-select__selected-item {
      span {
        color: #8d4bbb;
      }
    }
  }
  .antiquity {
    .el-select__selected-item {
      span {
        color: #f05654;
      }
    }
  }
}
</style>
